/**
 * Calendar Utilities - Phase X2
 * 
 * Helper functions for aircraft calendar functionality.
 */

import dayjs from 'dayjs';
import 'dayjs/locale/cs';

/**
 * Status color mapping for calendar events
 * Matches existing MissionCalendar.js convention
 */
export const STATUS_COLORS = {
  draft: '#9ca3af',      // gray-400
  ready: '#22c55e',      // green-500
  active: '#f97316',     // orange-500
  completed: '#3b82f6'   // blue-500
};

/**
 * Status labels for display
 */
export const STATUS_LABELS = {
  en: {
    draft: 'Draft',
    ready: 'Ready',
    active: 'In Progress',
    completed: 'Completed'
  },
  cs: {
    draft: 'Koncept',
    ready: 'Připraveno',
    active: 'Probíhá',
    completed: 'Dokončeno'
  }
};

/**
 * SAIL badge colors
 */
export const SAIL_COLORS = {
  'SAIL I': '#22c55e',   // green
  'SAIL II': '#eab308',  // yellow
  'SAIL III': '#f97316', // orange
  'SAIL IV': '#ef4444',  // red
  // Final iGRC exceeds Table 2/7's scope — Category C (Certified), not a Specific SAIL (see
  // SailLevel.OutOfScope). Without this key the badge fell through to the same gray default as
  // "unassessed", visually indistinguishable (2026-09-16 review round 2).
  'SAIL OUT_OF_SCOPE': '#e11d48', // rose-600
};

/** Badge label for a `SAIL <token>` event.sail_class — "OUT_OF_SCOPE" isn't a SAIL level a viewer
 * should read as one, so it gets a translated label instead of the raw wire token. `provisional`
 * (event.sail_provisional) appends a "*" marker — a normal in-scope Table 2 SAIL I-VI result looked
 * identical to a legacy MTOM×altitude one otherwise (2026-09-16 review round 2). */
export function getSailBadgeLabel(sailClass, language = 'en', provisional = false) {
  const base = sailClass === 'SAIL OUT_OF_SCOPE'
    ? (language === 'cs' ? 'Mimo SORA' : 'Out of SORA')
    : sailClass.replace('SAIL ', '');
  return provisional ? `${base}*` : base;
}

/**
 * Convert API events to react-big-calendar format
 */
export function toCalendarEvents(events, language = 'en') {
  return events.map(event => ({
    id: event.mission_id,
    title: event.mission_code,
    start: event.scheduled_start 
      ? new Date(event.scheduled_start) 
      : new Date(event.mission_date + 'T09:00:00Z'),
    end: event.scheduled_end 
      ? new Date(event.scheduled_end) 
      : dayjs(event.scheduled_start || event.mission_date).add(2, 'hour').toDate(),
    resource: {
      ...event,
      statusLabel: STATUS_LABELS[language]?.[event.status] || event.status
    }
  }));
}

/**
 * Get background color style for event based on status
 */
export function getEventStyle(event) {
  const status = event.resource?.status || 'draft';
  const backgroundColor = STATUS_COLORS[status] || STATUS_COLORS.draft;
  
  return {
    style: {
      backgroundColor,
      borderRadius: '4px',
      opacity: 0.9,
      color: '#fff',
      border: 'none',
      display: 'block'
    }
  };
}

/**
 * Format date range for display
 */
export function formatDateRange(start, end, language = 'en') {
  const loc = language === 'cs' ? 'cs' : 'en';
  dayjs.locale(loc);
  
  const startFormatted = dayjs(start).format('D. MMM');
  const endFormatted = dayjs(end).format('D. MMM YYYY');
  
  return `${startFormatted} - ${endFormatted}`;
}

/**
 * Calculate fleet timeline cell width based on view
 */
export function getFleetTimelineConfig(view) {
  switch (view) {
    case 'day':
      return {
        columnWidth: 60,  // px per hour
        columns: 24,      // hours
        headerFormat: 'HH:mm'
      };
    case 'week':
      return {
        columnWidth: 100, // px per day
        columns: 7,       // days
        headerFormat: 'ddd D'
      };
    case 'month':
    default:
      return {
        columnWidth: 40,  // px per day (dense)
        columns: 31,      // max days
        headerFormat: 'D'
      };
  }
}

/**
 * Telemetry status indicator
 */
export function getTelemetryIndicator(status) {
  switch (status) {
    case 'live':
      return { color: '#22c55e', label: 'Live', pulse: true };
    case 'stale':
      return { color: '#9ca3af', label: 'Offline', pulse: false };
    case 'never':
    default:
      return { color: '#d1d5db', label: 'No data', pulse: false };
  }
}

/**
 * Generate time slots for a day (06:00 - 22:00)
 */
export function generateDaySlots(date) {
  const slots = [];
  for (let hour = 6; hour <= 22; hour++) {
    slots.push({
      start: dayjs(date).hour(hour).minute(0).toDate(),
      end: dayjs(date).hour(hour + 1).minute(0).toDate(),
      label: dayjs(date).hour(hour).format('HH:mm')
    });
  }
  return slots;
}

/**
 * Check if two date ranges overlap
 */
export function dateRangesOverlap(start1, end1, start2, end2) {
  return start1 < end2 && start2 < end1;
}

/**
 * Calculate event position for timeline (percentage)
 */
export function calculateEventPosition(eventStart, eventEnd, rangeStart, rangeEnd) {
  const totalMs = rangeEnd.getTime() - rangeStart.getTime();
  const startOffset = Math.max(0, eventStart.getTime() - rangeStart.getTime());
  const endOffset = Math.min(totalMs, eventEnd.getTime() - rangeStart.getTime());
  
  return {
    left: (startOffset / totalMs) * 100,
    width: ((endOffset - startOffset) / totalMs) * 100
  };
}
